import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Link,
  FormControlLabel,
  Checkbox,
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  IconButton,
  Tooltip,
  InputAdornment,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import { v4 as uuidv4 } from "uuid";
import { useDispatch } from "react-redux";
import { cancelOrder } from "../../../redux/features/adminEventsSlice";

const REFUND_REASON_PRESETS = [
  "Customer requested refund",
  "Duplicate Order",
  "Order upgraded",
];
const REFUND_REASON_OTHER = "other";

const getOrderIdFromTicketOrder = (ticketOrder, orderId) => {
  if (!ticketOrder) return false;
  if (typeof ticketOrder === "string") {
    return ticketOrder.endsWith(`/orders/${orderId}`);
  }
  if (ticketOrder.id === orderId) return true;
  if (ticketOrder.path && ticketOrder.path.endsWith(`/orders/${orderId}`)) {
    return true;
  }
  return false;
};

const getAlreadyCancelledTicketIds = (order) => {
  const set = new Set();
  const instances = order?.cancellation?.instances ?? [];
  instances.forEach((inst) => {
    const tickets = inst.tickets ?? inst.ticketIds ?? [];
    tickets.forEach((t) =>
      set.add(typeof t === "string" ? t : t.ticketId)
    );
  });
  return set;
};

const getAttendeeDisplayName = (ticket) => {
  const fields = ticket.orderFormFields || [];
  const byKey = Object.fromEntries(
    fields.map((f) => [f.key, f?.value ?? ""]),
  );
  if (byKey.name && String(byKey.name).trim()) return String(byKey.name).trim();
  if (byKey.attendeeName && String(byKey.attendeeName).trim()) {
    return String(byKey.attendeeName).trim();
  }
  const first = (byKey.firstName && String(byKey.firstName).trim()) || "";
  const last = (byKey.lastName && String(byKey.lastName).trim()) || "";
  const combined = [first, last].filter(Boolean).join(" ");
  if (combined) return combined;
  return ticket.name || "Attendee";
};

const formatUsdCents = (usdCents) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format((usdCents || 0) / 100);
};

/** Allowed refund input: empty, or digits with optional decimal and up to 2 decimal places. */
const isValidRefundInput = (str) => /^\d*\.?\d{0,2}$/.test(String(str ?? ""));

/** Parse dollar string to usd cents, or null if invalid. */
const parseRefundDollarsToCents = (str) => {
  const s = String(str ?? "").trim().replace(/^\$/, "");
  if (s === "") return null;
  const n = parseFloat(s);
  if (Number.isNaN(n) || n < 0) return null;
  return Math.round(n * 100);
};

const buildTicketAmountsMap = (order, ticketsForOrder) => {
  const items = order?.items ?? [];
  const totalFeesUsdCents = order?.totalFeesUsdCents ?? 0;
  const promoCodeDiscountUsdCents = order?.promoCodeDiscountUsdCents ?? 0;
  const totalTickets = ticketsForOrder.length;
  const perTicketFeeCents =
    totalTickets > 0 ? Math.round(totalFeesUsdCents / totalTickets) : 0;
  const perTicketDiscountCents =
    totalTickets > 0
      ? Math.round(promoCodeDiscountUsdCents / totalTickets)
      : 0;
  const itemByTicketTypeKey = {};
  items.forEach((item) => {
    const key = item.ticketTypeKey;
    if (key != null) itemByTicketTypeKey[key] = item;
  });
  const map = {};
  ticketsForOrder.forEach((ticket) => {
    const item = itemByTicketTypeKey[ticket.ticketTypeKey];
    let ticketAmount = 0;
    if (item && item.quantity > 0) {
      ticketAmount = Math.round(item.totalUsdCents / item.quantity);
    }
    map[ticket.id] = Math.max(
      0,
      ticketAmount + perTicketFeeCents - perTicketDiscountCents
    );
  });
  return map;
};

const CancelOrderModal = ({
  open,
  onClose,
  order,
  event: eventProp,
  eventId: eventIdProp,
  organizationId,
  ticketsForOrder: ticketsForOrderProp,
  onSuccess,
}) => {
  const dispatch = useDispatch();
  const [selectTickets, setSelectTickets] = useState(false);
  const [selectedTicketIds, setSelectedTicketIds] = useState(new Set());
  const [refundReasonPreset, setRefundReasonPreset] = useState("");
  const [refundReasonOther, setRefundReasonOther] = useState("");
  const [emailRecipient, setEmailRecipient] = useState(false);
  const [emailAttendees, setEmailAttendees] = useState(false);
  const [customRefundAmount, setCustomRefundAmount] = useState("");
  const [customRefundInputOpen, setCustomRefundInputOpen] = useState(false);
  const [showConfirmStep, setShowConfirmStep] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [idempotencyKey, setIdempotencyKey] = useState(null);

  const event = eventProp || {};
  const eventId = event.id ?? eventIdProp;
  const orderId = order?.id;

  const ticketsForOrder = useMemo(() => {
    if (!orderId) return [];
    if (Array.isArray(ticketsForOrderProp)) {
      return ticketsForOrderProp;
    }
    const eventTickets = event.tickets || [];
    return eventTickets.filter((t) =>
      getOrderIdFromTicketOrder(t.order, orderId),
    );
  }, [orderId, ticketsForOrderProp, event.tickets]);

  const alreadyCancelled = useMemo(
    () => getAlreadyCancelledTicketIds(order),
    [order],
  );

  // Reset form state and generate idempotency key when modal opens
  useEffect(() => {
    if (open) {
      setIdempotencyKey(uuidv4());
      setShowConfirmStep(false);
      setSelectTickets(false);
      setSelectedTicketIds(new Set());
      setRefundReasonPreset("");
      setRefundReasonOther("");
      setEmailRecipient(false);
      setEmailAttendees(false);
      setCustomRefundAmount("");
      setCustomRefundInputOpen(false);
      setError(null);
    }
  }, [open, orderId]);

  const cancellableTickets = useMemo(
    () => ticketsForOrder.filter((t) => !alreadyCancelled.has(t.id)),
    [ticketsForOrder, alreadyCancelled],
  );

  const ticketAmountsMap = useMemo(
    () => buildTicketAmountsMap(order, ticketsForOrder),
    [order, ticketsForOrder],
  );

  const unrefundedRemainderUsdCents = useMemo(() => {
    const orderTotal = order?.totalUsdCents ?? 0;
    const instances = order?.cancellation?.instances ?? [];
    const alreadyRefunded = instances.reduce(
      (sum, inst) => sum + (inst.refundTotalUsdCents ?? 0),
      0,
    );
    return Math.max(0, orderTotal - alreadyRefunded);
  }, [order?.totalUsdCents, order?.cancellation?.instances]);

  const ticketIdsToCancel = useMemo(
    () =>
      selectTickets
        ? [...selectedTicketIds]
        : cancellableTickets.map((t) => t.id),
    [selectTickets, selectedTicketIds, cancellableTickets],
  );

  const calculatedRefundUsdCents = useMemo(() => {
    return ticketIdsToCancel.reduce(
      (sum, ticketId) => sum + (ticketAmountsMap[ticketId] ?? 0),
      0,
    );
  }, [ticketIdsToCancel, ticketAmountsMap]);

  const customRefundUsdCents = useMemo(
    () => parseRefundDollarsToCents(customRefundAmount),
    [customRefundAmount],
  );

  const totalRefundUsdCents = useMemo(() => {
    const raw =
      customRefundUsdCents != null ? customRefundUsdCents : calculatedRefundUsdCents;
    return Math.min(raw, unrefundedRemainderUsdCents);
  }, [
    customRefundUsdCents,
    calculatedRefundUsdCents,
    unrefundedRemainderUsdCents,
  ]);

  const isCustomRefund = customRefundUsdCents != null;

  const isRefundAmountOverMax =
    customRefundUsdCents != null &&
    customRefundUsdCents > unrefundedRemainderUsdCents;

  const handleToggleCancel = (ticketId) => {
    setSelectedTicketIds((prev) => {
      const next = new Set(prev);
      if (next.has(ticketId)) next.delete(ticketId);
      else next.add(ticketId);
      return next;
    });
  };

  const cancellationReasonDisplay =
    refundReasonPreset === REFUND_REASON_OTHER
      ? (refundReasonOther || "").trim()
      : refundReasonPreset;

  const handleContinueToConfirm = () => {
    if (isRefundAmountOverMax) {
      setError("Refund amount cannot exceed the remaining refundable balance.");
      return;
    }
    const hasTicketsToCancel = ticketIdsToCancel.length > 0;
    const hasRefundAmount = totalRefundUsdCents > 0;
    if (!hasTicketsToCancel && !hasRefundAmount) {
      setError(
        cancellableTickets.length === 0
          ? "Enter a refund amount to refund the remaining balance."
          : "Select at least one ticket to cancel or enter a refund amount."
      );
      return;
    }
    if (!refundReasonPreset) {
      setError("Please select a cancellation reason.");
      return;
    }
    if (
      refundReasonPreset === REFUND_REASON_OTHER &&
      !(refundReasonOther || "").trim()
    ) {
      setError("Please enter a cancellation reason.");
      return;
    }
    setError(null);
    setShowConfirmStep(true);
  };

  const handleSubmit = async () => {
    if (!idempotencyKey) {
      setError("Something went wrong. Please close the modal and try again.");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await dispatch(
        cancelOrder({
          organizationId,
          orderId,
          eventId,
          ticketIds: ticketIdsToCancel,
          refundAmountUsdCents: totalRefundUsdCents,
          cancellationReason: cancellationReasonDisplay || undefined,
          emailRecipient,
          emailAttendees,
          idempotencyKey,
        }),
      ).unwrap();
      onSuccess?.();
      onClose();
    } catch (e) {
      setError(e?.message ?? "Cancellation failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setShowConfirmStep(false);
      setSelectTickets(false);
      setSelectedTicketIds(new Set());
      setRefundReasonPreset("");
      setRefundReasonOther("");
      setCustomRefundAmount("");
      setCustomRefundInputOpen(false);
      setEmailRecipient(false);
      setEmailAttendees(false);
      setError(null);
      onClose();
    }
  };

  const handleBackFromConfirm = () => {
    setShowConfirmStep(false);
    setError(null);
  };

  if (!order) return null;

  const cancelledCount = alreadyCancelled.size;
  const totalTickets = ticketsForOrder.length;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle data-testid="cancel-order-modal-title">
        {showConfirmStep ? "Confirm cancellation" : `Cancel order ${orderId}`}
      </DialogTitle>
      <DialogContent>
        {showConfirmStep ? (
          <Box>
            <Typography variant="body1" sx={{ mb: 1.5 }}>
              Are you sure you want to cancel this order?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {ticketIdsToCancel.length > 0 && (
                <>{ticketIdsToCancel.length} ticket(s) will be cancelled.</>
              )}
              {ticketIdsToCancel.length > 0 && <br />}
              Amount to be refunded: {formatUsdCents(totalRefundUsdCents)}
            </Typography>
            {cancellationReasonDisplay && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Reason: {cancellationReasonDisplay}
              </Typography>
            )}
            <Typography
              variant="body2"
              color="error.main"
              fontWeight={600}
              sx={{ mt: 1.5 }}
              data-testid="cancel-order-cannot-undo"
            >
              This action cannot be undone.
            </Typography>
            {error && (
              <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                {error}
              </Typography>
            )}
          </Box>
        ) : unrefundedRemainderUsdCents === 0 && cancellableTickets.length === 0 ? (
          <Typography color="text.secondary">
            Order is fully cancelled and fully refunded. Nothing left to do.
          </Typography>
        ) : (
          <>
            {cancelledCount > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {cancelledCount} of {totalTickets} ticket(s) already cancelled.
              </Typography>
            )}
            {unrefundedRemainderUsdCents > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Remaining to refund: {formatUsdCents(unrefundedRemainderUsdCents)}
              </Typography>
            )}
            {cancellableTickets.length > 0 ? (
              selectTickets ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                    <Typography variant="body2" color="text.secondary">
                      Choose which tickets to cancel.
                    </Typography>
                    <Link
                      component="button"
                      type="button"
                      variant="body2"
                      onClick={() => setSelectTickets(false)}
                      data-testid="cancel-order-switch-to-all"
                    >
                      Cancel all {cancellableTickets.length} ticket(s) instead
                    </Link>
                  </Box>
                  {cancellableTickets.map((ticket) => {
                    const attendeeName = getAttendeeDisplayName(ticket);
                    const ticketPriceUsdCents = ticketAmountsMap[ticket.id] ?? 0;
                    const ticketTypeName = ticket.name || "Ticket";
                    return (
                      <Paper
                        key={ticket.id}
                        variant="outlined"
                        sx={{ p: 1.5 }}
                        data-testid={`cancel-order-ticket-${ticket.id}`}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.75,
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            fontWeight={600}
                            color="text.primary"
                          >
                            {ticketTypeName}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Attendee: {attendeeName}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Price: {formatUsdCents(ticketPriceUsdCents)}
                          </Typography>
                          <FormControlLabel
                            control={
                              <Checkbox
                                data-testid={`cancel-ticket-${ticket.id}`}
                                checked={selectedTicketIds.has(ticket.id)}
                                onChange={() => handleToggleCancel(ticket.id)}
                              />
                            }
                            label={
                              <Typography variant="body2">
                                Cancel this ticket
                              </Typography>
                            }
                          />
                        </Box>
                      </Paper>
                    );
                  })}
                </Box>
              ) : (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    This will cancel all {cancellableTickets.length} ticket(s).
                  </Typography>
                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    onClick={() => setSelectTickets(true)}
                    data-testid="cancel-order-select-tickets"
                  >
                    Select specific tickets instead
                  </Link>
                </Box>
              )
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                No tickets left to cancel.
                {unrefundedRemainderUsdCents > 0 &&
                  " Enter an amount below to refund the remainder."}
              </Typography>
            )}
            {unrefundedRemainderUsdCents > 0 && (
              <Box sx={{ mt: 3, mb: 2, display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
                {customRefundInputOpen ? (
                  <>
                    <TextField
                      size="small"
                      label="Refund amount"
                      placeholder="e.g. 25.00"
                      value={customRefundAmount}
                      onChange={(e) => {
                        const next = e.target.value;
                        if (isValidRefundInput(next)) setCustomRefundAmount(next);
                      }}
                      error={isRefundAmountOverMax}
                      helperText={
                        isRefundAmountOverMax
                          ? `Amount cannot exceed remaining refundable balance (max: ${formatUsdCents(unrefundedRemainderUsdCents)})`
                          : `Max: ${formatUsdCents(unrefundedRemainderUsdCents)}`
                      }
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">$</InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip title="Use calculated amount">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setCustomRefundInputOpen(false);
                                  setCustomRefundAmount("");
                                }}
                                aria-label="Use calculated amount"
                                data-testid="cancel-order-custom-refund-close"
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </InputAdornment>
                        ),
                      }}
                      inputProps={{
                        "data-testid": "cancel-order-custom-refund",
                        inputMode: "decimal",
                        pattern: "[0-9]*\\.?[0-9]{0,2}",
                      }}
                      sx={{ minWidth: 140 }}
                    />
                  </>
                ) : (
                  <>
                    <Typography
                      variant="subtitle2"
                      fontWeight={600}
                      data-testid="cancel-order-total-refund"
                    >
                      Amount to be refunded: {formatUsdCents(totalRefundUsdCents)}
                      {isCustomRefund && " (custom)"}
                    </Typography>
                    <Tooltip title="Enter custom refund amount">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setCustomRefundAmount(
                            (calculatedRefundUsdCents / 100).toFixed(2),
                          );
                          setCustomRefundInputOpen(true);
                        }}
                        aria-label="Enter custom refund amount"
                        data-testid="cancel-order-edit-refund"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
              </Box>
            )}
            <FormControl fullWidth size="small" sx={{ mt: 2, mb: 2 }} required>
              <InputLabel id="cancel-order-cancellation-reason-label">
                Cancellation reason
              </InputLabel>
              <Select
                labelId="cancel-order-cancellation-reason-label"
                label="Cancellation reason"
                value={refundReasonPreset}
                onChange={(e) => setRefundReasonPreset(e.target.value)}
                data-testid="cancel-order-cancellation-reason"
              >
                <MenuItem value="">
                  <em>Select a reason</em>
                </MenuItem>
                {REFUND_REASON_PRESETS.map((preset) => (
                  <MenuItem key={preset} value={preset}>
                    {preset}
                  </MenuItem>
                ))}
                <MenuItem value={REFUND_REASON_OTHER}>Other</MenuItem>
              </Select>
            </FormControl>
            {refundReasonPreset === REFUND_REASON_OTHER && (
              <TextField
                fullWidth
                size="small"
                label="Cancellation reason (other)"
                value={refundReasonOther}
                onChange={(e) => setRefundReasonOther(e.target.value)}
                placeholder="Enter cancellation reason"
                sx={{ mt: 0, mb: 2 }}
                data-testid="cancel-order-cancellation-reason-other"
              />
            )}
            <Box sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    data-testid="cancel-email-recipient"
                    checked={emailRecipient}
                    onChange={(e) => setEmailRecipient(e.target.checked)}
                  />
                }
                label="Email order recipient"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    data-testid="cancel-email-attendees"
                    checked={emailAttendees}
                    onChange={(e) => setEmailAttendees(e.target.checked)}
                  />
                }
                label="Email each attendee"
              />
            </Box>
            {error && (
              <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                {error}
              </Typography>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {showConfirmStep ? (
          <>
            <Button
              onClick={handleBackFromConfirm}
              disabled={submitting}
              data-testid="cancel-order-back"
            >
              Back
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleSubmit}
              disabled={submitting}
              data-testid="cancel-order-submit"
            >
              {submitting ? "Cancelling…" : "Yes, cancel order"}
            </Button>
          </>
        ) : (
          <>
            <Button onClick={handleClose} disabled={submitting}>
              Close
            </Button>
            {(cancellableTickets.length > 0 || unrefundedRemainderUsdCents > 0) &&
              (unrefundedRemainderUsdCents > 0 || cancellableTickets.length > 0) && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleContinueToConfirm}
                disabled={
                  (ticketIdsToCancel.length === 0 && totalRefundUsdCents === 0) ||
                  isRefundAmountOverMax ||
                  !refundReasonPreset ||
                  (refundReasonPreset === REFUND_REASON_OTHER &&
                    !(refundReasonOther || "").trim())
                }
                data-testid="cancel-order-review"
              >
                Review cancellation
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CancelOrderModal;
